import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getContact } from "../api";

const SAFE_PROTOCOLS = ["https:", "http:", "mailto:"];

/** An operator-entered link, or null when it is missing, malformed or not a web/mail link. */
export const safeSupportUrl = (url?: string | null) => {
  try {
    const parsed = new URL(url ?? "");
    return SAFE_PROTOCOLS.includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
};

/** The phone number inside a WhatsApp link (wa.me/60…, api.whatsapp.com/send?phone=60…), or null. */
export const whatsappNumber = (url?: string | null) => {
  try {
    const parsed = new URL(url ?? "");
    const digits =
      parsed.hostname === "wa.me"
        ? parsed.pathname.slice(1)
        : /(^|\.)whatsapp\.com$/.test(parsed.hostname)
        ? parsed.searchParams.get("phone") ?? ""
        : "";
    return /^\d{8,15}$/.test(digits) ? `+${digits}` : null;
  } catch {
    return null;
  }
};

/**
 * Support copy and links, shared by every support entry point.
 * `supportUrl` (support.contact_url) is for Top Up and Withdraw; `reviewUrl`
 * (registration.review_contact_url) is for everything else: the post-signup
 * popup, the side nav, the footer and the floating button.
 */
export const useContact = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language === "zh" ? "zh" : "en";
  const { data } = useQuery({
    queryKey: ["contact", locale],
    queryFn: () => getContact(locale),
    staleTime: 30_000,
    retry: false,
  });

  return {
    contact: data,
    supportUrl: safeSupportUrl(data?.url),
    reviewUrl: safeSupportUrl(data?.reviewUrl),
    /** `registration.review_contact_num`, else the number inside a WhatsApp `reviewUrl`. */
    reviewNumber: data?.reviewContactNum?.trim() || whatsappNumber(data?.reviewUrl),
  };
};
